import { CenteredScrollPage } from '@/components/shared/CenteredScrollPage';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  MIN_ACCOUNT_NAME_CHECK_LENGTH,
  useAccountNameAvailability,
  useSendSignupEmailCode,
  useSignupCheckout,
  useSignupPlanOffer,
  useVerifySignupEmailCode,
} from '@/hooks/useAuthQueries';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

function formatAud(value: string): string {
  return `$${Number(value).toFixed(2)}`;
}

const signupSchema = z
  .object({
    firstName: z.string().trim().min(1, 'First name is required'),
    surname: z.string().trim().min(1, 'Surname is required'),
    accountName: z.string().trim().min(1, 'Account name is required'),
    email: z.string().email('Invalid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
    usageIntent: z.enum(['PERSONAL', 'WORK', 'PERSONAL_AND_WORK']),
    businessHubName: z.string().trim().optional(),
    businessHubPhone: z.string().trim().optional(),
    businessHubWebsite: z.string().trim().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
  .superRefine((data, ctx) => {
    if (data.usageIntent !== 'PERSONAL' && !data.businessHubName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['businessHubName'],
        message: 'Business hub name is required',
      });
    }
  });

type SignupFormData = z.infer<typeof signupSchema>;

export default function Signup() {
  const signupMutation = useSignupCheckout();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const checkoutCancelled = searchParams.get('status') === 'cancel';

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    mode: 'onBlur',
    defaultValues: { usageIntent: 'PERSONAL_AND_WORK' },
  });

  const textFieldNames = [
    'firstName',
    'surname',
    'accountName',
    'email',
    'password',
    'confirmPassword',
    'businessHubName',
    'businessHubPhone',
    'businessHubWebsite',
  ] as const;

  // Browser autofill (e.g. Chrome's suggested password) writes values into the
  // DOM without firing events React Hook Form can observe.
  const syncBrowserFilledFields = (form: HTMLFormElement) => {
    for (const name of textFieldNames) {
      const field = form.elements.namedItem(name);
      if (field instanceof HTMLInputElement && field.value !== (getValues(name) ?? '')) {
        setValue(name, field.value, { shouldValidate: true, shouldDirty: true });
      }
    }
  };

  const usageIntent = watch('usageIntent');

  const accountNameValue = watch('accountName') ?? '';
  const debouncedAccountName = useDebouncedValue(accountNameValue, 400);
  const accountNameAvailability = useAccountNameAvailability(debouncedAccountName);
  const showAccountNameStatus =
    debouncedAccountName.trim().length >= MIN_ACCOUNT_NAME_CHECK_LENGTH;

  const emailValue = watch('email') ?? '';
  const debouncedEmail = useDebouncedValue(emailValue, 400);
  const planOffer = useSignupPlanOffer(debouncedEmail);
  const offer = planOffer.data;

  const sendCodeMutation = useSendSignupEmailCode();
  const verifyCodeMutation = useVerifySignupEmailCode();
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const [codeSentTo, setCodeSentTo] = useState<string | null>(null);
  const [codeValue, setCodeValue] = useState('');
  const [resendAvailableAt, setResendAvailableAt] = useState<Date | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  const emailIsValid = z.string().email().safeParse(emailValue).success;
  const emailVerified = verifiedEmail !== null && verifiedEmail === emailValue;

  useEffect(() => {
    if (!resendAvailableAt) {
      setCooldownSeconds(0);
      return;
    }
    const tick = () => {
      setCooldownSeconds(Math.max(0, Math.ceil((resendAvailableAt.getTime() - Date.now()) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [resendAvailableAt]);

  const resetSendCode = sendCodeMutation.reset;
  const resetVerifyCode = verifyCodeMutation.reset;
  useEffect(() => {
    if (verifiedEmail && emailValue !== verifiedEmail) {
      setVerifiedEmail(null);
    }
    if (codeSentTo && emailValue !== codeSentTo) {
      setCodeSentTo(null);
      setCodeValue('');
      setResendAvailableAt(null);
      resetSendCode();
      resetVerifyCode();
    }
  }, [emailValue, verifiedEmail, codeSentTo, resetSendCode, resetVerifyCode]);

  const handleSendCode = () => {
    sendCodeMutation.mutate(
      { email: emailValue },
      {
        onSuccess: (result) => {
          setCodeSentTo(emailValue);
          setCodeValue('');
          setResendAvailableAt(new Date(result.resendAvailableAt));
          verifyCodeMutation.reset();
        },
      }
    );
  };

  const handleVerifyCode = () => {
    verifyCodeMutation.mutate(
      { email: emailValue, code: codeValue.trim() },
      { onSuccess: () => setVerifiedEmail(emailValue) }
    );
  };

  useEffect(() => {
    if (signupMutation.data?.kind === 'checkout') {
      window.location.assign(signupMutation.data.url);
    } else if (signupMutation.data?.kind === 'complete') {
      navigate('/explore', { replace: true });
    }
  }, [signupMutation.data, navigate]);

  const onSubmit = (data: SignupFormData) => {
    if (verifiedEmail !== data.email) {
      return;
    }
    const {
      confirmPassword: _confirmPassword,
      businessHubName,
      businessHubPhone,
      businessHubWebsite,
      ...input
    } = data;
    signupMutation.mutate({
      ...input,
      businessHub:
        data.usageIntent !== 'PERSONAL' && businessHubName
          ? {
              name: businessHubName,
              phone: businessHubPhone || undefined,
              website: businessHubWebsite || undefined,
            }
          : undefined,
    });
  };

  return (
    <CenteredScrollPage>
      <Card className="flex max-h-[calc(100dvh-4rem)] w-full max-w-sm flex-col">
        <CardHeader className="shrink-0">
          <CardTitle>Create account</CardTitle>
          <CardDescription>Sign up to get started with Vivport</CardDescription>
        </CardHeader>
        <CardContent className="mx-6 mb-6 min-h-0 overflow-y-auto p-px pr-3">
          <form
            id="signup-form"
            onSubmit={(event) => {
              syncBrowserFilledFields(event.currentTarget);
              void handleSubmit(onSubmit)(event);
            }}
            onFocusCapture={(event) => syncBrowserFilledFields(event.currentTarget)}
            onClickCapture={(event) => syncBrowserFilledFields(event.currentTarget)}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Input
                  data-testid="signup-first-name"
                  placeholder="First name"
                  autoComplete="off"
                  {...register('firstName')}
                  disabled={signupMutation.isPending}
                />
                {errors.firstName && (
                  <p className="text-destructive text-sm mt-1">{errors.firstName.message}</p>
                )}
              </div>
              <div>
                <Input
                  data-testid="signup-surname"
                  placeholder="Surname"
                  autoComplete="off"
                  {...register('surname')}
                  disabled={signupMutation.isPending}
                />
                {errors.surname && (
                  <p className="text-destructive text-sm mt-1">{errors.surname.message}</p>
                )}
              </div>
            </div>

            <div>
              <Input
                data-testid="signup-account-name"
                placeholder="Account name"
                autoComplete="off"
                {...register('accountName')}
                disabled={signupMutation.isPending}
              />
              {errors.accountName && (
                <p className="text-destructive text-sm mt-1">{errors.accountName.message}</p>
              )}
              {showAccountNameStatus && (
                <div data-testid="signup-account-name-status" className="mt-1 text-sm">
                  {accountNameAvailability.isFetching ? (
                    <p className="text-muted-foreground">Checking availability…</p>
                  ) : accountNameAvailability.data?.available === true ? (
                    <p data-testid="signup-account-name-available" className="text-success">
                      Available
                    </p>
                  ) : accountNameAvailability.data?.available === false ? (
                    <div data-testid="signup-account-name-taken" className="text-destructive">
                      <p>That account name is taken.</p>
                      {accountNameAvailability.data.suggestions.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {accountNameAvailability.data.suggestions.map((suggestion) => (
                            <button
                              key={suggestion}
                              type="button"
                              data-testid="signup-account-name-suggestion"
                              onClick={() =>
                                setValue('accountName', suggestion, {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                })
                              }
                              className="rounded-md border border-input px-2 py-0.5 text-muted-foreground hover:text-primary"
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            <div>
              <Input
                data-testid="signup-email"
                type="email"
                placeholder="Email"
                autoComplete="username"
                {...register('email')}
                disabled={signupMutation.isPending}
              />
              {errors.email && (
                <p className="text-destructive text-sm mt-1">{errors.email.message}</p>
              )}
              {emailIsValid && (
                <div className="mt-2 space-y-2">
                  {emailVerified ? (
                    <p data-testid="signup-email-verified" className="text-success text-sm">
                      Email verified
                    </p>
                  ) : codeSentTo === emailValue ? (
                    <>
                      <p className="text-sm text-muted-foreground">
                        Enter the 6-digit code we emailed to {codeSentTo}.
                      </p>
                      <div className="flex gap-2">
                        <Input
                          data-testid="signup-email-code-input"
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="6-digit code"
                          autoComplete="one-time-code"
                          value={codeValue}
                          onChange={(event) => setCodeValue(event.target.value)}
                          disabled={verifyCodeMutation.isPending}
                        />
                        <Button
                          type="button"
                          data-testid="signup-email-code-verify"
                          onClick={handleVerifyCode}
                          disabled={verifyCodeMutation.isPending || codeValue.trim().length !== 6}
                        >
                          {verifyCodeMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Verify'
                          )}
                        </Button>
                      </div>
                      <Button
                        type="button"
                        variant="link"
                        data-testid="signup-email-code-resend"
                        onClick={handleSendCode}
                        disabled={sendCodeMutation.isPending || cooldownSeconds > 0}
                        className="h-auto p-0 text-sm text-muted-foreground hover:text-primary"
                      >
                        {cooldownSeconds > 0 ? `Resend code (${cooldownSeconds}s)` : 'Resend code'}
                      </Button>
                      {verifyCodeMutation.isError && (
                        <p data-testid="signup-email-code-error" className="text-destructive text-sm">
                          {verifyCodeMutation.error?.message || 'Verification failed. Please try again.'}
                        </p>
                      )}
                      {sendCodeMutation.isError && (
                        <p className="text-destructive text-sm">
                          {sendCodeMutation.error?.message || 'Could not send the code. Please try again.'}
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid="signup-email-code-send"
                        onClick={handleSendCode}
                        disabled={sendCodeMutation.isPending}
                        className="w-full"
                      >
                        {sendCodeMutation.isPending ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Sending code...
                          </>
                        ) : (
                          'Send verification code'
                        )}
                      </Button>
                      <p className="text-xs text-muted-foreground">
                        We'll email you a code to confirm this address before you sign up.
                      </p>
                      {sendCodeMutation.isError && (
                        <p data-testid="signup-email-code-error" className="text-destructive text-sm">
                          {sendCodeMutation.error?.message || 'Could not send the code. Please try again.'}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <div>
              <Input
                data-testid="signup-password"
                type="password"
                placeholder="Password"
                autoComplete="new-password"
                {...register('password')}
                disabled={signupMutation.isPending}
              />
              {errors.password && (
                <p className="text-destructive text-sm mt-1">{errors.password.message}</p>
              )}
            </div>

            <div>
              <Input
                data-testid="signup-confirm-password"
                type="password"
                placeholder="Confirm password"
                autoComplete="new-password"
                {...register('confirmPassword')}
                disabled={signupMutation.isPending}
              />
              {errors.confirmPassword && (
                <p className="text-destructive text-sm mt-1">{errors.confirmPassword.message}</p>
              )}
            </div>

            <div>
              <Label className="text-sm">Using for</Label>
              <Controller
                control={control}
                name="usageIntent"
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="mt-1 space-y-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="PERSONAL" id="usage-personal" data-testid="signup-usage-personal" />
                      <Label htmlFor="usage-personal" className="cursor-pointer font-normal">Personal use</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="WORK" id="usage-work" data-testid="signup-usage-work" />
                      <Label htmlFor="usage-work" className="cursor-pointer font-normal">Work</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="PERSONAL_AND_WORK" id="usage-both" data-testid="signup-usage-both" />
                      <Label htmlFor="usage-both" className="cursor-pointer font-normal">Personal & work</Label>
                    </div>
                  </RadioGroup>
                )}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Everything is included in your subscription.
              </p>
            </div>

            {usageIntent !== 'PERSONAL' && (
              <div className="space-y-2">
                <Label className="text-sm">Business hub details</Label>
                <div>
                  <Input
                    data-testid="signup-business-name"
                    placeholder="Business hub name"
                    autoComplete="off"
                    {...register('businessHubName')}
                    disabled={signupMutation.isPending}
                  />
                  {errors.businessHubName && (
                    <p className="text-destructive text-sm mt-1">{errors.businessHubName.message}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    data-testid="signup-business-phone"
                    placeholder="Phone (optional)"
                    autoComplete="off"
                    {...register('businessHubPhone')}
                    disabled={signupMutation.isPending}
                  />
                  <Input
                    data-testid="signup-business-website"
                    placeholder="Website (optional)"
                    autoComplete="off"
                    {...register('businessHubWebsite')}
                    disabled={signupMutation.isPending}
                  />
                </div>
              </div>
            )}

            {usageIntent === 'WORK' && (
              <p data-testid="signup-personal-note" className="text-xs text-muted-foreground">
                You can still use Vivport personally at any time — at no extra cost.
              </p>
            )}

            <div className="p-3 border rounded-lg space-y-1">
              <p className="text-sm font-medium">Monthly subscription</p>
              {planOffer.isError ? (
                <p data-testid="signup-plan-error" className="text-sm text-destructive">
                  We couldn't load the subscription price. Please try again shortly.
                </p>
              ) : offer ? (
                <div className="text-sm text-muted-foreground space-y-1">
                  {offer.discountPercent > 0 ? (
                    <p data-testid="signup-plan-price">
                      <span className="line-through mr-2">{formatAud(offer.priceAud)}/month</span>
                      <span className="text-foreground font-medium">
                        {formatAud(offer.effectiveFirstChargeAud)}/month
                      </span>
                      <span className="ml-2">({offer.discountPercent}% discount applied)</span>
                    </p>
                  ) : (
                    <p data-testid="signup-plan-price" className="text-foreground font-medium">
                      {formatAud(offer.priceAud)}/month
                    </p>
                  )}
                  {offer.discountPercent === 100 && (
                    <p data-testid="signup-plan-full-discount">
                      Your email has a 100% discount — you won't be charged and no payment method
                      is needed.
                    </p>
                  )}
                  <p>
                    Your first month includes {formatAud(offer.firstMonthCreditAud)} of
                    AI usage credit.
                    {offer.discountPercent !== 100 &&
                      " You'll pay securely with Stripe before your account is created."}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Loading plan details…</p>
              )}
            </div>

            {checkoutCancelled && (
              <p data-testid="signup-checkout-cancelled" className="text-sm text-muted-foreground">
                Checkout cancelled — no account was created. Sign up again when you're ready.
              </p>
            )}
          </form>
        </CardContent>
        <CardFooter className="shrink-0 flex-col">
          <Button
            data-testid="signup-button"
            type="submit"
            form="signup-form"
            className="w-full"
            disabled
          >
            Sign up - Coming soon
          </Button>
          {!emailVerified && (
            <p
              data-testid="signup-verify-hint"
              className="mt-2 text-center text-xs text-muted-foreground"
            >
              Verify your email address above to enable sign up.
            </p>
          )}

          <div className="mt-2 text-center">
            <Button
              variant="link"
              onClick={() => navigate('/login')}
              disabled={signupMutation.isPending}
              className="text-sm text-muted-foreground hover:text-primary p-0 h-auto"
            >
              Already have an account? Log in
            </Button>
          </div>

          {signupMutation.isError && (
            <p data-testid="signup-error" className="text-destructive mt-2 text-sm">
              {signupMutation.error?.message || 'Signup failed. Please try again.'}
            </p>
          )}
        </CardFooter>
      </Card>
    </CenteredScrollPage>
  );
}
